import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Gift } from "lucide-react";
import { trackEvent } from "../lib/tracking";
import { consumeSuppressFormPopup } from "../lib/scrollToSection";
import { writeItem, writeJSON } from "../lib/safeStorage";
import { useSettings } from "../lib/settings";
import {
  shouldSuppress, LS_DISMISSED_AT, LS_SUBMITTED, SS_SHOWN_SESSION,
} from "../lib/leadPopup";

// ---- Tunables (cerute explicit de utilizator) ----
const SCROLL_THRESHOLD_DESKTOP = 0.55; // 55% on desktop
const SCROLL_THRESHOLD_MOBILE = 0.65;  // 65% on mobile
const TIME_THRESHOLD_MS = 30_000;
const MIN_SCROLL_FOR_TIMER = 0.20;     // timer fires only if user scrolled ≥ 20%
const MIN_SCROLL_FOR_EXIT_INTENT = 0.15;
const MOBILE_BREAKPOINT = 768;

// Storage keys. The three the gate also reads live in `lib/leadPopup`, so a
// key can never be written here under one name and read there under another.
const LS_TRIGGER_TYPE = "popupTriggerType";
const SS_TRIGGER_TYPE = "popupTriggerType";

// Custom event LeadForm dispatches when a lead is successfully created.
const LEAD_SUBMITTED_EVENT = "fped:lead-submitted";

/** Returns the user's current scroll progress as a value in [0, 1]. */
function scrollProgress() {
  const total = document.documentElement.scrollHeight - window.innerHeight;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, window.scrollY / total));
}

/** True when #lead-form is at least partially visible in viewport. */
function isLeadFormInView() {
  const el = document.getElementById("lead-form");
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
}

export default function ExitIntent() {
  const location = useLocation();
  const { lead_popup_enabled: enabled } = useSettings();
  const [open, setOpen] = useState(false);
  const triggered = useRef(false);
  const triggerTypeRef = useRef(null);     // "scroll" | "timer" | "exit_intent"
  const maxScrollRef = useRef(0);
  const pathRef = useRef(location.pathname);

  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);

  const fireOnce = useCallback((triggerType) => {
    if (triggered.current) return;
    if (shouldSuppress(pathRef.current, enabled)) return;
    // M910 — explicit CTA navigation pre-sets a one-shot suppress flag.
    // We consume it here so the next regular (scroll-driven) page interaction
    // works exactly as before — only THIS firing is skipped.
    if (consumeSuppressFormPopup()) {
      triggered.current = true; // never fire on this page-view either
      return;
    }
    triggered.current = true;
    triggerTypeRef.current = triggerType;
    writeItem("session", SS_SHOWN_SESSION, "1");
    writeItem("session", SS_TRIGGER_TYPE, triggerType);
    writeItem("local", LS_TRIGGER_TYPE, triggerType);
    trackEvent("popup_shown", { trigger_type: triggerType });
    setOpen(true);
  }, [enabled]);

  useEffect(() => {
    if (shouldSuppress(location.pathname, enabled)) return;

    let timerId = null;
    let scrollRaf = null;
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const scrollThreshold = isMobile ? SCROLL_THRESHOLD_MOBILE : SCROLL_THRESHOLD_DESKTOP;

    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = null;
        const progress = scrollProgress();
        if (progress > maxScrollRef.current) maxScrollRef.current = progress;
        if (progress >= scrollThreshold) fireOnce("scroll");
      });
    };

    const onMouseLeave = (e) => {
      if (e.clientY > 0) return;
      // Suppress exit-intent if user hasn't engaged yet OR is already on the lead form.
      if (maxScrollRef.current < MIN_SCROLL_FOR_EXIT_INTENT) return;
      if (isLeadFormInView()) return;
      fireOnce("exit_intent");
    };

    const onTimer = () => {
      // 30s gate: only fire if user has scrolled at least MIN_SCROLL_FOR_TIMER.
      if (maxScrollRef.current >= MIN_SCROLL_FOR_TIMER) {
        fireOnce("timer");
      }
    };

    const onLeadSubmitted = () => {
      writeItem("local", LS_SUBMITTED, "1");
      const tt = triggerTypeRef.current;
      if (open && tt) {
        trackEvent("popup_submitted", { trigger_type: tt });
      }
      setOpen(false);
      triggered.current = true;
    };

    timerId = window.setTimeout(onTimer, TIME_THRESHOLD_MS);
    window.addEventListener("scroll", onScroll, { passive: true });

    const isFinePointer = typeof window.matchMedia === "function"
      && window.matchMedia("(pointer: fine)").matches;
    if (isFinePointer) {
      document.addEventListener("mouseleave", onMouseLeave);
    }
    window.addEventListener(LEAD_SUBMITTED_EVENT, onLeadSubmitted);

    return () => {
      if (timerId) window.clearTimeout(timerId);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener(LEAD_SUBMITTED_EVENT, onLeadSubmitted);
    };
  }, [location.pathname, fireOnce, open, enabled]);

  const handleDismiss = () => {
    writeItem("local", LS_DISMISSED_AT, String(Date.now()));
    if (triggerTypeRef.current) {
      trackEvent("popup_dismissed", { trigger_type: triggerTypeRef.current });
    }
    setOpen(false);
  };

  const goToForm = () => {
    if (triggerTypeRef.current) {
      trackEvent("popup_cta_clicked", { trigger_type: triggerTypeRef.current });
    }
    // Stash attribution in sessionStorage so the form, when submitted, knows
    // the lead came via the popup and which trigger fired it. LeadForm reads
    // and clears this on successful submit.
    writeJSON("session", "popupAttribution", {
      source: "popup",
      popup_trigger_type: triggerTypeRef.current || null,
      popup_cta_clicked: true,
    });
    setOpen(false);
    setTimeout(
      () => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" }),
      50,
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleDismiss();
        else setOpen(next);
      }}
    >
      <DialogContent
        className="bg-[#F5F6F6] border-[#E5E7EB] rounded-2xl max-w-lg"
        data-testid="exit-intent-modal"
      >
        <DialogHeader>
          <div className="h-12 w-12 rounded-xl bg-[#965EA7] grid place-items-center text-white mb-2">
            <Gift size={22} />
          </div>
          <DialogTitle className="font-display text-2xl text-[#373A45]">
            Stai puțin! Ia broșura cursului – gratuit.
          </DialogTitle>
          <DialogDescription className="text-[#6B7280]">
            Îți trimitem pe email broșura de prezentare a cursului și toate detaliile despre înscriere.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mt-2">
          <Button
            onClick={goToForm}
            className="flex-1 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold py-5"
            data-testid="exit-intent-cta"
          >
            Trimite-mi broșura
          </Button>
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="rounded-full border-2 border-[#373A45] text-[#373A45] hover:bg-[#373A45] hover:text-white font-semibold py-5"
            data-testid="exit-intent-close"
          >
            Mai târziu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Helper that LeadForm/Checkout calls after a successful submit. */
export function notifyLeadSubmitted() {
  writeItem("local", LS_SUBMITTED, "1");
  try { window.dispatchEvent(new CustomEvent(LEAD_SUBMITTED_EVENT)); } catch { /* ignore */ }
}
